(() => {
  const TRANSITION_MS = 400;
  const MOBILE_QUERY = '(max-width: 1024px)';

  const initBox = (root) => {
    if (root.dataset.gm3BoxReady === 'true') return;
    root.dataset.gm3BoxReady = 'true';

    const select = root.querySelector('[data-gm3-box-select]');
    const selectWrap = root.querySelector('.gm3-box__select-wrap');
    const selectButton = root.querySelector('[data-gm3-box-select-button]');
    const selectText = root.querySelector('[data-gm3-box-select-text]');
    const selectOptions = Array.from(root.querySelectorAll('[data-gm3-box-select-option]'));
    const viewport = root.querySelector('[data-gm3-box-viewport]');
    const track = root.querySelector('[data-gm3-box-track]');
    const allCards = Array.from(track ? track.querySelectorAll('[data-gm3-box-card]') : []);
    const mediaQuery = window.matchMedia(MOBILE_QUERY);

    if (!viewport || !track || allCards.length === 0) return;

    let cards = [];
    let activeIndex = 0;
    let draggableInstance = null;
    let dragBounds = null;
    let resizeTimer = null;

    const openCartDrawer = () => {
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }

      document.getElementById('cart-drawer-toggle')?.click();
    };

    const refreshCart = () => {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    };
    const dispatchAddToCartTracking = (button, product) => {
      document.dispatchEvent(new CustomEvent('ga4:add_to_cart', {
        detail: {
          button,
          product,
          quantity: 1,
        },
      }));
    };

    const addVariantToCart = async (button) => {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
          }),
        });

        const state = await response.json();
        if (!response.ok) {
          throw new Error(state.description || state.message || 'Unable to add item to cart.');
        }

        refreshCart();
        openCartDrawer();
        dispatchAddToCartTracking(button, state);
      } catch (error) {
        console.warn('[gm3-box] add to cart failed', error);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      }
    };

    const getCurrentX = () => {
      if (!window.gsap) return 0;
      const val = window.gsap.getProperty(track, 'x');
      return typeof val === 'number' ? val : 0;
    };

    const getPadInline = () =>
      parseFloat(window.getComputedStyle(track).paddingInlineStart) || 0;

    const computeMaxX = () => {
      if (cards.length === 0) return 0;
      const padInline = getPadInline();
      const firstLeft = cards[0].getBoundingClientRect().left;
      const viewportLeft = viewport.getBoundingClientRect().left;
      return getCurrentX() + viewportLeft + padInline - firstLeft;
    };

    const computeMinX = () => {
      if (cards.length === 0) return 0;
      const padInline = getPadInline();
      const lastRight = cards[cards.length - 1].getBoundingClientRect().right;
      const viewportRight = viewport.getBoundingClientRect().right;
      return getCurrentX() + (viewportRight - padInline) - lastRight;
    };

    const computeBounds = () => {
      const maxX = computeMaxX();
      const minX = computeMinX();
      return { minX: Math.min(minX, maxX), maxX: Math.max(minX, maxX) };
    };

    const clampToBounds = (x, bounds = computeBounds()) =>
      Math.max(bounds.minX, Math.min(bounds.maxX, x));

    const getCardTargetX = (card, bounds = computeBounds()) => {
      const padInline = getPadInline();
      const rawTargetX = -(card.offsetLeft - padInline);
      return clampToBounds(rawTargetX, bounds);
    };

    const getClosestSnapIndex = (x = getCurrentX()) => {
      const bounds = computeBounds();
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const targetX = getCardTargetX(card, bounds);
        const distance = Math.abs(targetX - x);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updatePositionState = (x = getCurrentX()) => {
      activeIndex = getClosestSnapIndex(x);
    };

    const setActive = (index, animate = true) => {
      if (cards.length === 0) return;
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));

      if (!window.gsap || !mediaQuery.matches) {
        if (window.gsap) window.gsap.set(track, { x: 0 });
        return;
      }

      const card = cards[activeIndex];
      const padInline = getPadInline();
      const rawTargetX =
        getCurrentX() + viewport.getBoundingClientRect().left + padInline - card.getBoundingClientRect().left;
      const targetX = clampToBounds(rawTargetX);

      draggableInstance?.[0]?.disable();
      window.gsap.to(track, {
        x: targetX,
        duration: animate ? TRANSITION_MS / 1000 : 0,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
          draggableInstance?.[0]?.enable();
          updatePositionState();
        },
      });
    };

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const closeSelect = () => {
      selectWrap?.classList.remove('is-open');
      selectButton?.setAttribute('aria-expanded', 'false');
    };

    const openSelect = () => {
      selectWrap?.classList.add('is-open');
      selectButton?.setAttribute('aria-expanded', 'true');
    };

    const updateSelectState = () => {
      const selectedValue = select?.value || '';
      const activeOption = selectOptions.find((option) => option.dataset.value === selectedValue);
      if (activeOption && selectText) selectText.textContent = activeOption.textContent.trim();

      selectOptions.forEach((option) => {
        const active = option.dataset.value === selectedValue;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const refreshCards = () => {
      const selectedOptionValue = select?.value || '';

      allCards.forEach((card) => {
        const match = !selectedOptionValue || card.dataset.gm3BoxOption === selectedOptionValue;
        card.classList.toggle('is-hidden', !match);
      });

      cards = allCards.filter((card) => !card.classList.contains('is-hidden'));
      activeIndex = 0;

      if (window.gsap) {
        window.gsap.killTweensOf(track);
        window.gsap.set(track, { x: 0 });
      }

      requestAnimationFrame(() => setActive(0, false));
    };

    const setupDraggable = () => {
      if (!window.gsap || !window.Draggable || draggableInstance) return;

      draggableInstance = window.Draggable.create(track, {
        type: 'x',
        minimumMovement: 5,
        cursor: 'grab',
        activeCursor: 'grabbing',
        onDragStart() {
          if (!mediaQuery.matches || cards.length < 2) {
            this.endDrag();
            return;
          }

          window.gsap.killTweensOf(track);
          dragBounds = computeBounds();
        },
        onDrag() {
          if (!dragBounds) return;
          const x = getCurrentX();
          const limitedX = clampToBounds(x, dragBounds);
          if (limitedX !== x) window.gsap.set(track, { x: limitedX });
        },
        onDragEnd() {
          if (!dragBounds) return;
          dragBounds = null;
          window.gsap.set(track, { x: clampToBounds(getCurrentX()) });
          updatePositionState();
        },
      });
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-gm3-box-add]');
      if (!button || !root.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      addVariantToCart(button);
    });

    selectButton?.addEventListener('click', (event) => {
      event.preventDefault();
      if (selectWrap?.classList.contains('is-open')) {
        closeSelect();
      } else {
        openSelect();
      }
    });

    selectOptions.forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault();
        if (!select) return;

        select.value = option.dataset.value || '';
        updateSelectState();
        closeSelect();
        refreshCards();
      });
    });

    document.addEventListener('click', (event) => {
      if (!selectWrap || selectWrap.contains(event.target)) return;
      closeSelect();
    });

    select?.addEventListener('change', () => {
      updateSelectState();
      refreshCards();
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!mediaQuery.matches || cards.length < 2 || !window.gsap) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        window.gsap.killTweensOf(track);
        window.gsap.set(track, { x: clampToBounds(getCurrentX() - deltaX) });
        updatePositionState();
      },
      { passive: false }
    );

    track.addEventListener('dragstart', (event) => event.preventDefault());

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => setActive(activeIndex, false), 150);
    });

    const handleMediaChange = () => {
      setupDraggable();
      setActive(activeIndex, false);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    track.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => setActive(activeIndex, false), { once: true });
    });

    setupDraggable();
    updateSelectState();
    refreshCards();
    document.fonts?.ready?.then(() => setActive(activeIndex, false)).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-box]').forEach(initBox);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
